import axios, { AxiosInstance } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance, apiRequest } from '@/auth/msalConfig';

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // repeat format: key=val1&key=val2 (ASP.NET Core [FromQuery] binding)
  },
});

const getAccessToken = async (): Promise<string | null> => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  const request = { ...apiRequest, account: accounts[0] };

  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('==[Auth] Silent token acquisition failed, triggering interactive flow');
      try {
        await msalInstance.acquireTokenRedirect(request);
      } catch (redirectError) {
        console.error('==[Auth] Interactive token acquisition failed:', redirectError);
      }
    } else {
      console.error('==[Auth] Unexpected token acquisition error:', error);
    }
    return null;
  }
};

api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.warn('==[Auth] Received 401, attempting re-authentication');
        try {
          await msalInstance.acquireTokenRedirect({
            ...apiRequest,
            account: accounts[0],
          });
        } catch (redirectError) {
          console.error('==[Auth] Re-authentication failed:', redirectError);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;
